<template>
  <div
    ref="root"
    :class="{ narrow: content.items.length === 1 }"
    class="key-points"
  >
    <ul class="key-points-list">
      <li v-for="(item, i) in content.items" :key="i" class="key-points__item">
        <template v-if="item">
          <img
            v-if="item.picture"
            v-src="item.picture"
            :alt="item.picture.alt"
            :width="item.picture.width"
            :height="item.picture.height"
            :sets="['320']"
            class="key-points__item__picture"
          />

          <span v-if="item.label" class="key-points__item__headline">
            {{ item.label }}
          </span>

          <div class="key-points__item__title">
            <div class="key-points__item__title__original">
              {{ item.value }}
            </div>

            <div ref="splits" class="key-points__item__title__split">
              {{ item.value }}
            </div>
          </div>
          <span v-if="item.subtitle" class="section-label">
            {{ item.subtitle }}
          </span>

          <div
            v-if="item.description"
            class="key-points__item__title__description p-medium"
          >
            {{ item.description }}
          </div>
        </template>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { gsap } from 'gsap'
import { SplitText } from 'gsap/SplitText'
import { onMounted, PropType, ref } from 'vue'

import { useAppear } from '@/utils/aware'
import { Picture } from '@/types'

export interface Point {
  label: string
  value: string
  description?: string
  subtitle?: string
  picture?: Picture
}

export interface KeyPoints {
  items: Point[]
}

defineProps({
  content: {
    type: Object as PropType<KeyPoints>,
    required: true,
  },
})

const isNumber = (item: string) => !isNaN(Number(item))

const maxAnimatedNumbers = 3
// eslint-disable-next-line id-length
const animatedNumbersCollection: HTMLElement[] = []
const root = ref<HTMLElement | null>(null)
const splits = ref<HTMLElement[]>([])
const initSplitNumbers = () => {
  splits.value.forEach(item => {
    if (!isNumber(item.innerText)) {
      return
    }

    const numbers = new SplitText(item, {
      type: 'chars',
    })

    const numberOnly = numbers.chars.filter(number =>
      isNumber(number.innerHTML)
    )
    const numbersAmount =
      numberOnly.length >= maxAnimatedNumbers
        ? maxAnimatedNumbers
        : numberOnly.length
    const animatedNumbers = numberOnly.slice(-numbersAmount)

    animatedNumbers.forEach(el => {
      const number = Number(el.innerHTML)
      const numbersContainer = document.createElement('div')
      numbersContainer.classList.add('animated-number-container')

      el.innerHTML = `<div class="animated-number-original">${el.innerHTML}</div>`
      el.classList.add('animated-number')
      el.appendChild(numbersContainer)

      if (number > 0) {
        for (let i = 1; i <= number; i++) {
          const div = document.createElement('div')

          div.innerText = `${i}`
          numbersContainer.appendChild(div)
        }
      } else {
        const div = document.createElement('div')

        div.innerText = `${number}`
        numbersContainer.appendChild(div)
      }

      animatedNumbersCollection.push(numbersContainer)
    })
  })
}

useAppear(root, ({ isInViewport }) => {
  if (isInViewport) {
    gsap.to(animatedNumbersCollection, {
      duration: 1.5,
      yPercent: -100,
      stagger: 0.2,
      ease: 'power1.out',
    })
  }
})

onMounted(() => {
  initSplitNumbers()
})
</script>

<style lang="scss" scoped>
$bp: s;

.key-points {
  &.narrow {
    /* stylelint-disable-next-line declaration-no-important */
    width: min(90%, 60rem) !important;
    margin-right: auto;
    margin-left: auto;
  }
}

.key-points-list {
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  border: 1px solid var(--c-foreground);

  @include mq($bp) {
    flex-direction: row;
    justify-content: center;
  }
}

.key-points__item {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex: 1;
  min-height: 24rem;
  margin: 0;
  padding: 0 20px;
  list-style-type: none;

  &::before {
    display: none;
  }

  &:not(:last-child) {
    border-bottom: 1px solid var(--c-foreground);
  }

  @include mq($bp) {
    &:not(:last-child) {
      border-right: 1px solid var(--c-foreground);
      border-bottom: 0;
    }
  }
}

.key-points__item__picture {
  max-width: min(15rem, 50vw);
  height: auto;
  aspect-ratio: 1;
  margin-bottom: 1rem;
}

.key-points__item__headline {
  margin-bottom: 1.5rem;
  padding: 0 2rem;
  font-size: 1rem;
  // line-height: 3rem;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.key-points__item__title {
  position: relative;
  font-size: 5rem;
  line-height: 6rem;
  letter-spacing: 0.5px;
}

.key-points__item__title__original {
  @extend %fw-black;

  font-family: $ff-alt;
  opacity: 0;
}

.key-points__item__title__split {
  @extend %fw-black;

  position: absolute;
  top: 0;
  display: flex;
  justify-content: center;
  width: 100%;
  font-family: $ff-alt;
  text-align: center;

  :deep(.animated-number) {
    position: relative;
    overflow: hidden;

    &.is-padded {
      padding-right: 0.6rem;
    }
  }

  :deep(.animated-number-original) {
    opacity: 0;
  }

  :deep(.animated-number-container) {
    position: absolute;
    top: 100%;
  }
}

.key-points__item__title__description {
  max-width: 35rem;
  line-height: 2.5rem;
  text-align: center;
}
</style>
